import type { ReactNode } from "react";

export const W = 480;
export const H = 280;
export const M = { left: 56, right: 20, top: 16, bottom: 34 };

// Validated categorical palette (dataviz validate_palette.js, light + dark, adjacent pairs):
// blue, amber, violet, green — in this fixed order, never cycled.
export const SERIES = [
  { name: "Group A", fill: "fill-[#1E4FD8] dark:fill-[#4A90D9]", stroke: "stroke-[#1E4FD8] dark:stroke-[#4A90D9]", swatch: "bg-[#1E4FD8] dark:bg-[#4A90D9]" },
  { name: "Group B", fill: "fill-[#C2710C] dark:fill-[#D47A1F]", stroke: "stroke-[#C2710C] dark:stroke-[#D47A1F]", swatch: "bg-[#C2710C] dark:bg-[#D47A1F]" },
  { name: "Group C", fill: "fill-[#8B5CF6] dark:fill-[#A67BE0]", stroke: "stroke-[#8B5CF6] dark:stroke-[#A67BE0]", swatch: "bg-[#8B5CF6] dark:bg-[#A67BE0]" },
  { name: "Group D", fill: "fill-[#2D8A5E] dark:fill-[#3FA37A]", stroke: "stroke-[#2D8A5E] dark:stroke-[#3FA37A]", swatch: "bg-[#2D8A5E] dark:bg-[#3FA37A]" },
] as const;

// Bar with a 4px rounded data-end and a square baseline.
export function TopRoundedBar(x: number, y: number, w: number, h: number, r = 4): string {
  const rr = Math.min(r, w / 2, h);
  return `M${x} ${y + h} V${y + rr} Q${x} ${y} ${x + rr} ${y} H${x + w - rr} Q${x + w} ${y} ${x + w} ${y + rr} V${y + h} Z`;
}

// Surface ring colour for overlapping marks, matching the chart card background.
export const SURFACE_STROKE = "stroke-white dark:stroke-[#0D0D10]";

export function LinearScale(domain: [number, number], range: [number, number]) {
  const [d0, d1] = domain, [r0, r1] = range;
  return (v: number) => r0 + ((v - d0) / (d1 - d0)) * (r1 - r0);
}

export function NiceTicks(min: number, max: number, count = 5): number[] {
  const step = 10 ** Math.floor(Math.log10((max - min) / count));
  const nice = [1, 2, 5, 10].map((m) => m * step).find((s) => (max - min) / s <= count) ?? step * 10;
  const ticks: number[] = [];
  for (let v = Math.ceil(min / nice) * nice; v <= max + 1e-9; v += nice) ticks.push(Math.round(v * 1e6) / 1e6);
  return ticks;
}

// Rounds a data range outward to the same "nice" step NiceTicks would use.
export function NiceDomain(min: number, max: number, count = 5): [number, number] {
  const step = 10 ** Math.floor(Math.log10((max - min) / count));
  const nice = [1, 2, 5, 10].map((m) => m * step).find((s) => (max - min) / s <= count) ?? step * 10;
  return [Math.floor(min / nice) * nice, Math.ceil(max / nice) * nice];
}

const GRID ="stroke-[#0A0A0F]/10 dark:stroke-white/10";
const TICK_TEXT = "fill-[#5C5C66] dark:fill-[#9A9A9A] text-[10px]";

export function Axes({ xTicks, yTicks, x, y, xLabel, yLabel, yFormat = String }: {
  xTicks: number[]; yTicks: number[]; x: (v: number) => number; y: (v: number) => number;
  xLabel: string; yLabel: string; yFormat?: (v: number) => string;
}) {
  return (
    <g aria-hidden>
      {yTicks.map((t) => (
        <g key={`y${t}`}>
          <line x1={M.left} x2={W - M.right} y1={y(t)} y2={y(t)} className={GRID} />
          <text x={M.left - 8} y={y(t)} dy="0.32em" textAnchor="end" className={TICK_TEXT}>{yFormat(t)}</text>
        </g>
      ))}
      {xTicks.map((t) => (
        <text key={`x${t}`} x={x(t)} y={H - M.bottom + 16} textAnchor="middle" className={TICK_TEXT}>{t}</text>
      ))}
      <text x={(M.left + W - M.right) / 2} y={H - 4} textAnchor="middle" className={TICK_TEXT}>{xLabel}</text>
      <text transform={`translate(12 ${(M.top + H - M.bottom) / 2}) rotate(-90)`} textAnchor="middle" className={TICK_TEXT}>{yLabel}</text>
    </g>
  );
}

export interface TooltipState { x: number; y: number; lines: string[] }

// Positioned in viewBox units, converted to percentages so it tracks the responsive SVG.
export function Tooltip({ tip }: { tip: TooltipState | null }) {
  if (!tip) return null;
  const flip = tip.x > W * 0.6;
  return (
    <div
      className="pointer-events-none absolute z-10 rounded-lg border border-[#0A0A0F]/10 dark:border-white/10 bg-white/95 dark:bg-[#15151A]/95 px-3 py-2 shadow-lg text-xs text-[#0A0A0F] dark:text-white whitespace-nowrap"
      style={{ left: `${(tip.x / W) * 100}%`, top: `${(tip.y / H) * 100}%`, transform: `translate(${flip ? "calc(-100% - 12px)" : "12px"}, -50%)` }}
    >
      {tip.lines.map((l, i) => <p key={l} className={i === 0 ? "font-medium" : "text-[#5C5C66] dark:text-[#9A9A9A]"}>{l}</p>)}
    </div>
  );
}

export function Legend({ items }: { items: { label: string; swatch: string }[] }) {
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-[#5C5C66] dark:text-[#9A9A9A]">
      {items.map(({ label, swatch }) => (
        <span key={label} className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-sm ${swatch}`} />{label}</span>
      ))}
    </div>
  );
}

export function ChartSvg({ label, children, onLeave }: { label: string; children: ReactNode; onLeave?: () => void }) {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label} className="w-full h-auto select-none" onMouseLeave={onLeave}>
      {children}
    </svg>
  );
}

// Converts a pointer event to viewBox x, for crosshair charts.
export function PointerToViewBoxX(e: React.MouseEvent<SVGElement>): number {
  const svg = (e.currentTarget as SVGElement).ownerSVGElement ?? (e.currentTarget as SVGSVGElement);
  const rect = svg.getBoundingClientRect();
  return ((e.clientX - rect.left) / rect.width) * W;
}
